import {
  SalesOpportunity,
  SalesOpportunityListResult,
  SalesOpportunityPipeline,
} from '../../services/salesOpportunitiesApi';
import { OPPORTUNITY_STAGES, OpportunityStageId } from '../../types/OpportunityStage';

const OPEN_STAGE_IDS = OPPORTUNITY_STAGES.filter(
  (stage) => stage.id !== 'Won' && stage.id !== 'Lost',
).map((stage) => stage.id);

export function buildPipelineFromItems(items: SalesOpportunity[]): SalesOpportunityPipeline {
  const stages = OPEN_STAGE_IDS.map((stageId) => {
    const stageItems = items.filter((item) => item.stage === stageId);
    return {
      stage: stageId,
      count: stageItems.length,
      totalAmount: stageItems.reduce((sum, item) => sum + item.amount, 0),
      totalMonthlyRecurring: stageItems.reduce((sum, item) => sum + item.monthlyRecurringAmount, 0),
    };
  });

  const openItems = items.filter((item) => item.stage !== 'Won' && item.stage !== 'Lost');

  return {
    stages,
    pipelineValue: openItems.reduce((sum, item) => sum + item.amount, 0),
    totalCount: items.length,
  };
}

export function moveOpportunityInBoard(
  data: SalesOpportunityListResult,
  opportunityId: string,
  stage: OpportunityStageId,
  sortOrder: number,
): SalesOpportunityListResult {
  const items = data.items.map((item) =>
    item.id === opportunityId ? { ...item, stage, sortOrder } : item,
  );

  return {
    items,
    pipeline: buildPipelineFromItems(items),
  };
}

export function upsertOpportunityInBoard(
  data: SalesOpportunityListResult,
  opportunity: SalesOpportunity,
): SalesOpportunityListResult {
  const exists = data.items.some((item) => item.id === opportunity.id);
  const items = exists
    ? data.items.map((item) => (item.id === opportunity.id ? opportunity : item))
    : [...data.items, opportunity];

  return {
    items,
    pipeline: buildPipelineFromItems(items),
  };
}

export function removeOpportunityFromBoard(
  data: SalesOpportunityListResult,
  opportunityId: string,
): SalesOpportunityListResult {
  const items = data.items.filter((item) => item.id !== opportunityId);
  return {
    items,
    pipeline: buildPipelineFromItems(items),
  };
}

export type OpportunityBoardFilterState = {
  search: string;
  ownerUserId: string;
  stage: string;
};

export function filterOpportunityItems(
  items: SalesOpportunity[],
  filters: OpportunityBoardFilterState,
): SalesOpportunity[] {
  const search = filters.search.trim().toLowerCase();
  const ownerUserId = filters.ownerUserId.trim();
  const stage = filters.stage.trim();

  return items.filter((item) => {
    if (ownerUserId && item.ownerUserId !== ownerUserId) {
      return false;
    }

    if (stage && item.stage !== stage) {
      return false;
    }

    if (!search) {
      return true;
    }

    const haystack = [
      item.title,
      item.companyName,
      item.clientName,
      item.description,
      item.marketSegmentName,
      item.projectName,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(search);
  });
}

export function applyOpportunityBoardFilters(
  data: SalesOpportunityListResult,
  filters: OpportunityBoardFilterState,
): SalesOpportunityListResult {
  const items = filterOpportunityItems(data.items, filters);
  return {
    items,
    pipeline: buildPipelineFromItems(items),
  };
}
